import type { SearchData } from './saved-search-data';

export const SAVED_SEARCH_INTENT_PARAM = 'resume_search';
export const SAVED_SEARCH_INTENT_STORAGE_KEY = 'saved_search_intent';
export const SAVED_SEARCH_INTENT_TTL_MS = 30 * 60 * 1000;

type SavedSearchIntent = {
  token: string;
  path: string;
  source: string;
  data: SearchData;
  createdAt: number;
};

function parsePath(path: string) {
  if (!path.startsWith('/') || path.startsWith('//')) return null;

  const url = new URL(path, 'https://getapet.com');
  const token = url.searchParams.get(SAVED_SEARCH_INTENT_PARAM);
  url.searchParams.delete(SAVED_SEARCH_INTENT_PARAM);
  url.searchParams.sort();

  return { url, token, path: `${url.pathname}${url.search}${url.hash}` };
}

export function createSavedSearchIntentStore({
  storage,
  now = Date.now,
  createToken = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`,
}: {
  storage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  now?: () => number;
  createToken?: () => string;
}) {
  let pending: SavedSearchIntent | null = null;
  let storageUnavailable = false;

  function write(intent: SavedSearchIntent | null) {
    pending = intent;
    try {
      storage.setItem(SAVED_SEARCH_INTENT_STORAGE_KEY, JSON.stringify(intent));
    } catch {
      storageUnavailable = true;
    }
  }

  function read() {
    if (!storageUnavailable) {
      try {
        const stored = storage.getItem(SAVED_SEARCH_INTENT_STORAGE_KEY);
        if (stored !== null) pending = JSON.parse(stored);
      } catch {
        // Storage must not prevent login; native can keep the intent in memory.
      }
    }

    if (
      !pending ||
      typeof pending.token !== 'string' ||
      typeof pending.path !== 'string' ||
      typeof pending.source !== 'string' ||
      !pending.data ||
      typeof pending.data !== 'object' ||
      !Number.isFinite(pending.createdAt) ||
      now() - pending.createdAt < 0 ||
      now() - pending.createdAt >= SAVED_SEARCH_INTENT_TTL_MS
    ) {
      write(null);
    }

    return pending;
  }

  return {
    start(path: string, source: string, data: SearchData) {
      const parsed = parsePath(path);
      if (!parsed) return path;

      const token = createToken();
      write({
        token,
        path: parsed.path,
        source,
        data: JSON.parse(JSON.stringify(data)),
        createdAt: now(),
      });
      parsed.url.searchParams.set(SAVED_SEARCH_INTENT_PARAM, token);
      return `${parsed.url.pathname}${parsed.url.search}${parsed.url.hash}`;
    },

    take(path: string, source: string) {
      const parsed = parsePath(path);
      if (!parsed?.token) return null;

      const intent = read();
      if (
        !intent ||
        intent.token !== parsed.token ||
        intent.path !== parsed.path ||
        intent.source !== source
      ) {
        return null;
      }

      // Consume before opening: several SearchModal placements may mount together.
      write(null);
      return intent.data;
    },
  };
}

// The marker is only added to the login return URL. Cancelling login therefore
// cannot reopen a modal. Session storage survives web login's full page reload;
// native keeps the same intent in memory while its login page is on the stack.
export const savedSearchIntent = createSavedSearchIntentStore({
  storage: {
    getItem: (key) =>
      (typeof window !== 'undefined' ? window.sessionStorage?.getItem(key) : null) ?? null,
    setItem: (key, value) => {
      if (typeof window !== 'undefined') window.sessionStorage?.setItem(key, value);
    },
  },
});
